<script>
// Larguras das linhas, com títulos de tamanhos diferentes entre si — barras
// iguais leem como tabela, não como conversas. Os números imitam o que os dois
// campos produzem de fato: título de 137 a 260px, e a hora de "há 3h" (~28px)
// a "há 16 min" (52px, medido no navegador) pelo `formatConversationDate`.
// Pedir mais linhas do que o catálogo tem repete o ciclo.
export const LARGURAS_DAS_LINHAS = [
  { titulo: '72%', data: '30px' },
  { titulo: '54%', data: '52px' },
  { titulo: '84%', data: '28px' },
  { titulo: '46%', data: '46px' },
  { titulo: '68%', data: '30px' },
  { titulo: '58%', data: '38px' },
]

/**
 * Linhas-fantasma da lista de conversas da lateral — o carregando da primeira
 * pintura de Recentes E o rodapé da rolagem infinita, com UMA anatomia.
 *
 * ★ Existe para as duas cargas terem a mesma cara e a mesma caixa: na
 * navegação cada linha mede os 34px da conversa carregada, então trocar
 * esqueleto por linha real não muda a altura da lista (nem o `scrollTop`).
 *
 * Só desenha. Quem decide quantas linhas e em que modo é `chat-sidebar.vue`.
 */
export default {
  name: 'IntelligenceV2ChatSidebarEsqueleto',
  props: {
    quantidade: {
      type: Number,
      default: LARGURAS_DAS_LINHAS.length,
    },
    // Anatomia da navegação (`/ia`): sem o quadrado do ícone (a linha limpa não
    // tem balão) e com a coluna da hora à direita (a linha carregada tem).
    // No histórico é o contrário: ícone à esquerda e o meta num texto só.
    navegacao: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    linhas() {
      const total = Math.max(0, this.quantidade)
      const linhas = []
      for (let i = 0; i < total; i++) {
        linhas.push({
          chave: 'l' + i,
          ...LARGURAS_DAS_LINHAS[i % LARGURAS_DAS_LINHAS.length],
        })
      }
      return linhas
    },
  },
}
</script>

<template>
  <div
    class="chat-sidebar-esqueleto"
    :class="{ 'chat-sidebar-esqueleto--navegacao': navegacao }"
    aria-hidden="true"
  >
    <div
      v-for="linha in linhas"
      :key="linha.chave"
      class="chat-sidebar-esqueleto__linha ia-shimmer-pulso"
    >
      <BaseLoadingSkeleton
        v-if="!navegacao"
        class="chat-sidebar-esqueleto__icone"
        width="14px"
        height="14px"
      />
      <div class="chat-sidebar-esqueleto__titulo">
        <BaseLoadingSkeleton :width="linha.titulo" height="12px" />
      </div>
      <div v-if="navegacao" class="chat-sidebar-esqueleto__data">
        <BaseLoadingSkeleton :width="linha.data" height="10px" />
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
// A caixa INTEIRA da conversa carregada na navegação, padding incluso (o reset
// do projeto é `border-box`). Medida no navegador: 34.2px. Deduzir
// 13px × 1.35 + 18 dá outro número e erra — mudou o padding da linha em
// `_ia-sidebar.sass`, meça de novo em vez de recalcular.
// Travado em `chat-sidebar-esqueleto.unit.js` contra o padding e o recuo da
// linha real em `_ia-sidebar.sass`.
$altura-da-linha-navegacao: 34px

.chat-sidebar-esqueleto__linha
  display: flex
  align-items: center
  // Mesmo respiro da `conversation-row`: 8px entre ícone e título, 4px de
  // altura por linha.
  gap: 8px
  padding: 4px 0
  // O pulso é a classe GLOBAL `ia-shimmer-pulso` no template
  // (`design/_ia-shimmer.sass` — keyframe `ia-shimmer`, opacidade, composita
  // na GPU; `prefers-reduced-motion` mora lá). A varredura de
  // `background-position` do `BaseLoadingSkeleton` repinta por quadro, e
  // nesta lateral que re-renderiza enquanto carrega lê como travamento; aqui
  // ela é desligada e o que sobra é a pílula neutra pulsando.
  ::v-deep .base-loading-skeleton::before
    animation: none

  // Retângulo de 6px empilhado lê como formulário; em pílula lê como texto
  // ausente, que é o que é.
  ::v-deep .base-loading-skeleton,
  ::v-deep .base-loading-skeleton::before
    border-radius: 999px

// O `BaseLoadingSkeleton` embrulha o bloco num `div` sem largura própria; como
// item de flex ele encolhe a zero e a barra some. Quem cresce é este container
// — a percentagem então mede a linha, como o título real mede.
.chat-sidebar-esqueleto__titulo
  flex: 1
  min-width: 0

// Espelha o `.conversation-row__date` da linha carregada: não encolhe
// (`flex: 0 0 auto`) e fica a 8px do título. Sem isto o título ocupava a
// largura inteira e o esqueleto prometia uma linha sem hora — que não é a
// que chega.
.chat-sidebar-esqueleto__data
  flex: 0 0 auto
  margin-left: 8px

// ★ Na navegação a linha tem de ter a MESMA caixa da conversa carregada, senão
// a lista pula de altura quando ela sai — e um esqueleto que empurra o
// conteúdo é pior que nenhum. O recuo espelha a linha (`padding-left: 16px`,
// de `_ia-sidebar.sass`) e o `min-height` fecha a altura: a barra de 12px
// sozinha dá 30px contra os 34 da conversa.
.chat-sidebar-esqueleto--navegacao .chat-sidebar-esqueleto__linha
  min-height: $altura-da-linha-navegacao
  padding: 9px 8px 9px 16px
</style>
